<template>
  <section :class="{ 'lg:flex' : !isModal }">
    <main class="p-4" :class="{ 'lg:w-3/4' : !isModal }">
    <header>
        <h2 class="font-bold text-gray-800">Transfer Balance Account Transactions</h2>
        <!-- eslint-disable-next-line max-len -->
        <p class="mt-1 w-3/4 text-gray-600 text-sm leading-tight">Enter the amounts that have been added to (or subtracted from) the member's Transfer Balance Account. These will be used to determine the highest ever balance of the Transfer Balance Account at 30 June 2021 and 30 June 2023 which in turn determine the level of indexation provided at 1 July 2021 and 1 July 2023.</p>
      </header>
      <section class="mt-4">
        <TransferBalanceCapAddTransaction
          @add-transaction="handleAddTransaction"
        />
        <TransferBalanceCapTransactions
          :transactions="updatedTransactions"
          @delete-transaction="handleDeleteTransaction"
        />
      </section>
    </main>
    <aside
      class="flex-0 p-4 bg-gray-200 "
      :class="{ 'lg:w-1/4 flex flex-col justify-center items-center' : !isModal }"
    >
      <div :class="{ 'flex space-x-4' : isLoggedIn}">
        <TransferBalanceCapIndexation
          v-for="indexation in indexations"
          :key="indexation.startDate.getFullYear()"
          :year="indexation.startDate.getFullYear()"
          :newTBC="indexation.tbc"
          :indexation="indexation.indexation"
          :maxTBA="indexation.maxTBA"
          :unused="indexation.unused"
          :ratio="indexation.ratio"
        />
        <div v-if="isLoggedIn" class="w-1/2">
          <h2 class="font-bold text-gray-900 border-b border-gray-400 pb-1">Download PDF</h2>
          <m-form class="mt-4">
            <p class="text-gray-600 text-sm">Download the indexation Calculation.</p>
            <m-input label="Name" v-model="name" class="mt-1" id="name" />
            <m-button @click="showPreview" class="hidden">Preview</m-button>
            <m-button class="mt-2" @click="handlePDFDownload">Download</m-button>
          </m-form>
        </div>

        <m-modal
          v-if='isLoggedIn'
          id='preview-modal'
          :show='showPreviewModal'
          @close='showPreviewModal = false'
        >
          <transfer-balance-cap-pdf
            id='pdfContent'
            ref='pdfContent'
            :indexations='indexations'
            :transactions='updatedTransactions'
            :name='name'
            :preparedOn='today'
          ></transfer-balance-cap-pdf>
          <div class='my-8 text-right'>
            <m-button
              @click='showPreviewModal = false'
            >Close
            </m-button>
          </div>
        </m-modal>
      </div>
    </aside>
  </section>
</template>

<script>
import _ from 'lodash';
import { API, ENDPOINTS } from '@/app/services/api-url';
import EventBus from '@/app/eventBus';
import TransferBalanceCapTransactions from '../../components/transfer-balance-cap-transactions/transfer-balance-cap-transactions.vue';
import TransferBalanceCapAddTransaction from '../../components/transfer-balance-cap-add-transaction/transfer-balance-cap-add-transaction.vue';
import TransferBalanceCapIndexation from '../../components/transfer-balance-cap-indexation/transfer-balance-cap-indexation.vue';
import calculateTBC from '../../../../../../utils/calculators/tbc';
// eslint-disable-next-line max-len
import TransferBalanceCapPdf from '../../components/transfer-balance-cap-pdf/transfer-balance-cap-pdf.vue';

export default {
  name: 'transfer-balance-cap-container',
  components: {
    TransferBalanceCapTransactions,
    TransferBalanceCapAddTransaction,
    TransferBalanceCapIndexation,
    TransferBalanceCapPdf,
  },
  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    today: {
      type: Date,
      default: () => new Date(),
    },
  },
  data() {
    return {
      showPreviewModal: false,
      name: '',
      transactions: [],
      tbcTransactions: [],
      tbcCalculations: [],
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters['auth/loggedIn'];
    },
    indexations() {
      return _.orderBy(this.tbcCalculations.filter(indexation => indexation.endDate > new Date(2021, 6, 1)), 'endDate', 'desc');
    },
    updatedTransactions() {
      const transactions = _.cloneDeep(this.transactions);
      const trnx = _(transactions)
        .keyBy('id')
        .merge(_.keyBy(this.tbcTransactions, 'id'))
        .values()
        .value();
      return _.orderBy(trnx, 'longDate', 'asc');
    },
  },
  watch: {
    transactions: {
      immediate: true,
      handler(val) {
        [this.tbcTransactions, this.tbcCalculations] = calculateTBC(_.orderBy(val, 'date'));
      },
    },
  },
  methods: {
    handleAddTransaction(transaction) {
      const transactionID = this.transactions.length + 1;
      this.transactions.push({
        id: transactionID,
        ...transaction,
      });
    },
    handleDeleteTransaction(transaction) {
      this.transactions = this.transactions.filter(item => transaction.id !== item.id);
    },
    showPreview() {
      this.showPreviewModal = !this.showPreviewModal;
    },
    formatContent(content) {
      const tailwindLink = document.createElement('link');
      tailwindLink.href = 'https://unpkg.com/tailwindcss@^1.0/dist/tailwind.min.css';
      tailwindLink.rel = 'stylesheet';
      content.prepend(tailwindLink);
      return content;
    },
    getContent() {
      const content = document.getElementById('pdfContent').cloneNode(true);
      return this.formatContent(content);
    },
    getPdfOptions() {
      return {
        format: 'A4',
        displayHeaderFooter: false,
        headerTemplate: '',
        footerTemplate: '',
        landscape: true,
        margin: {
          top: '100',
          left: '80',
          right: '80',
          bottom: '100',
        },
      };
    },
    handlePDFDownload() {
      const content = this.getContent();
      const pdfOptions = this.getPdfOptions();

      API.post(ENDPOINTS.POST_RENDER_PDF, {
        content: content.innerHTML,
        pdfOptions: JSON.stringify(pdfOptions),
      }).then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `tbc_indexation-${new Date().toLocaleDateString().trim()}.pdf`);
        document.body.appendChild(link);
        link.click();
      }).catch((err) => {
        console.log(err);
      });

      EventBus.$emit('addNotification', {
        title: 'Download Requested!',
        icon: 'tick',
        message: 'Your PDF(s) will download automatically when ready!',
      });

      this.$mixpanel.track('tbc-pdf-requested', {
        name: this.name,
      });
    },
  },
};
</script>

<style>

</style>
